// 26 ADM-020 to ADM-025: the config viewer and the approval queue. A ⚖ key takes effect only once
// an owner approves; the server enforces it (05 DAT-T08). Each request shows its impact note
// (ADM-022). Approving needs a reason and a fresh code (ADM-003). During a live event an approved
// change waits until the event ends, unless the owner marks it "emergency" and types the setting's
// name (ADM-023).
// Stage 2 (decision record 0071): the group "Fair play numbers" (ADM-080, ADM-095, ADM-104, ADM-107);
// each request shows the current value, the proposed value, the allowed range and the owner file
// (ADM-080); beside a waiting ⚖ change, the server's own verdict, the proofs it feeds and, for a
// thought, the option-worth pass against the line (ADM-081, ADM-095 point 2, ADM-107 point 2). The
// verdict comes from ops.admin_config_checks(), the same guard ops.config_approve_request runs.
import type { SupabaseClient } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { verifyCode } from '../auth/mfa';
import { adminText } from '../lib/copy';
import { messageFor, runCommand } from '../lib/commands';
import { ProofState } from '../components/ProofState';
import { Breakable } from '../components/Text';
import { ThoughtTable } from '../components/ThoughtTable';
import { inFairPlayGroup, pct, PROOF_NAMES, THOUGHTS, thoughtName } from '../lib/fairplay';
import { NBSP, VALUE_BREAKS } from '../lib/tidy';

interface ConfigRow {
  key: string;
  value: unknown;
  fair: boolean;
  version: number;
}
interface KeyFacts {
  is_fairness: boolean;
  min_value?: number | null;
  max_value?: number | null;
  owner_file?: string | null;
}
/** A many-to-one embed: one key row, or a list of one. */
type Embed = KeyFacts | KeyFacts[] | null | undefined;
const factsOf = (e: Embed): KeyFacts | null => (Array.isArray(e) ? (e[0] ?? null) : (e ?? null));

/** An active version with its key's ⚖ mark. */
interface ActiveRow {
  key: string;
  value: unknown;
  version: number;
  config_keys: Embed;
}
const toConfigRow = (r: ActiveRow): ConfigRow => ({
  key: r.key,
  value: r.value,
  version: r.version,
  fair: !!factsOf(r.config_keys)?.is_fairness,
});

/** 26 ADM-081: the one-cap harness's run with the proposed value (packages/training src/capNote.ts). */
interface OneCapRun {
  passed: boolean;
  free_weeks: number;
  spender_weeks: number;
  time_saved: number;
  gap_shots: number;
  breaks_one_cap: boolean;
  line?: number;
}

interface OptionWorth {
  status?: string;
  thought_shares?: Record<string, number>;
}
export interface ChangeRow {
  id: string;
  key: string;
  kind?: string;
  current_value?: unknown;
  proposed_value: unknown;
  reason: string;
  status: string;
  impact_note: {
    text?: string;
    option_worth?: OptionWorth;
    proofs?: { one_cap?: OneCapRun };
  } | null;
  batch_code?: string | null;
  config_keys?: Embed;
}

/** The server's verdict on one waiting ⚖ change (ops.admin_config_checks). */
export interface Check {
  guard: string | null;
  proofs: { proof_key: string; state: 'green' | 'red' | 'not_run' }[];
  thought_line: number | null;
}

/** 30 SEED-150: the requests that wait as one batch (each stage's first ⚖ numbers), by batch. */
export function batches(queue: ChangeRow[]): Map<string, ChangeRow[]> {
  const m = new Map<string, ChangeRow[]>();
  for (const c of queue) if (c.batch_code) m.set(c.batch_code, [...(m.get(c.batch_code) ?? []), c]);
  return m;
}

const isFair = (c: ChangeRow) => !!factsOf(c.config_keys)?.is_fairness;
/** ADM-080: is this request listed under "Fair play numbers"? */
export const inGroup = (c: ChangeRow) => inFairPlayGroup(c.key, isFair(c));

/** "Stage 2: its first fair-play numbers" for stage2_initial (SEED-150, version 2). */
export function batchTitle(code: string): string {
  const m = /^stage(\d+)_initial$/.exec(code);
  return m ? adminText('config.batch_heading', { stage: Number(m[1]) }) : code;
}

const show = (v: unknown) => (v === undefined ? '—' : JSON.stringify(v));

function rangeText(k: KeyFacts | null, oneCap = false): string {
  const lo = k?.min_value;
  const hi = k?.max_value;
  // a number that feeds the one cap has no range of its own: the one-cap run decides it (ADM-081)
  if (lo == null && hi == null)
    return adminText(oneCap ? 'config.range_one_cap' : 'config.range_none');
  if (lo != null && hi != null && lo === hi) return adminText('config.range_fixed', { v: lo });
  return adminText('config.range', { min: lo ?? '—', max: hi ?? '—' });
}

/** One reason and one fresh code approve a whole batch (30 SEED-150; 26 ADM-003, ADM-021). */
function BatchApproveForm({
  sb,
  code: batchCode,
  items,
  onDone,
}: {
  sb: SupabaseClient;
  code: string;
  items: ChangeRow[];
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [needCode, setNeedCode] = useState(false);
  const [msg, setMsg] = useState('');
  const fair = items.filter(inGroup);
  const rest = items.filter((c) => !inGroup(c));
  const list = (rows: ChangeRow[]) => (
    <ul className="plain pairs">
      {rows.map((c) => (
        <li key={c.id}>
          <code>
            <Breakable text={c.key} />
          </code>{' '}
          {/* "=" and its value stay together (a no-break space) when the line wraps */}
          {`=${NBSP}`}
          <code>
            <Breakable text={show(c.proposed_value)} at={VALUE_BREAKS} />
          </code>
        </li>
      ))}
    </ul>
  );
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (needCode && !(await verifyCode(sb, code.trim())))
          return setMsg('That code did not work. Wait for a new code, then try again.');
        if (needCode) await sb.auth.refreshSession();
        const r = await runCommand(sb, 'config.approve_batch', { batch_code: batchCode }, reason);
        setNeedCode(r.error === 'step_up_required');
        setMsg(messageFor(r));
        if (r.ok) onDone();
      }}
    >
      <p>{adminText('config.batch_intro', { n: items.length })}</p>
      <details>
        <summary>{adminText('config.batch_see_all', { n: items.length })}</summary>
        {fair.length > 0 && (
          <>
            <h4>{adminText('config.group_fair', { n: fair.length })}</h4>
            {list(fair)}
          </>
        )}
        {rest.length > 0 && (
          <>
            <h4>{adminText('config.group_batch_other', { n: rest.length })}</h4>
            {list(rest)}
          </>
        )}
      </details>
      <label htmlFor={`reason-${batchCode}`}>Why you approve them</label>
      <input
        id={`reason-${batchCode}`}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        required
      />
      {needCode && (
        <>
          <label htmlFor={`code-${batchCode}`}>6-digit code</label>
          <input
            id={`code-${batchCode}`}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
        </>
      )}
      <p>
        <button className="primary" type="submit">
          {adminText('config.batch_approve', { n: items.length })}
        </button>
      </p>
      <p role="status">{msg}</p>
    </form>
  );
}

/** The arguments of config.approve; an emergency carries the setting's name as typed. */
export function approveArgs(
  requestId: string,
  emergency: boolean,
  typedKey: string,
): Record<string, unknown> {
  return emergency
    ? { request_id: requestId, emergency: true, confirm_key: typedKey.trim() }
    : { request_id: requestId };
}

function ApproveForm({
  sb,
  c,
  onDone,
  blockedBy,
}: {
  sb: SupabaseClient;
  c: ChangeRow;
  onDone: () => void;
  blockedBy?: string;
}) {
  const [reason, setReason] = useState('');
  const [emergency, setEmergency] = useState(false);
  const [typedKey, setTypedKey] = useState('');
  const [code, setCode] = useState('');
  const [needCode, setNeedCode] = useState(false);
  const [msg, setMsg] = useState('');
  const id = c.id.slice(0, 8);
  // A change the server would refuse shows only why, beside its switched-off Approve: nothing to fill in.
  if (blockedBy)
    return (
      <div>
        <p className="warn" id={`why-${id}`}>
          {messageFor({ ok: false, error: blockedBy })}
        </p>
        <p>
          <button className="primary" type="button" disabled aria-describedby={`why-${id}`}>
            Approve
          </button>
        </p>
      </div>
    );
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (needCode && !(await verifyCode(sb, code.trim())))
          return setMsg('That code did not work. Wait for a new code, then try again.');
        if (needCode) await sb.auth.refreshSession();
        const r = await runCommand(
          sb,
          'config.approve',
          approveArgs(c.id, emergency, typedKey),
          reason,
        );
        setNeedCode(r.error === 'step_up_required');
        setMsg(messageFor(r));
        if (r.ok) onDone();
      }}
    >
      <label htmlFor={`reason-${id}`}>Why you approve it</label>
      <input
        id={`reason-${id}`}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        required
      />
      <label className="check">
        <input
          type="checkbox"
          checked={emergency}
          onChange={(e) => setEmergency(e.target.checked)}
        />
        <span>{adminText('config.emergency')}</span>
      </label>
      {emergency && (
        <>
          <label htmlFor={`key-${id}`}>Type the setting name to confirm</label>
          <input
            id={`key-${id}`}
            value={typedKey}
            onChange={(e) => setTypedKey(e.target.value)}
            required
          />
        </>
      )}
      {needCode && (
        <>
          <label htmlFor={`code-${id}`}>6-digit code</label>
          <input
            id={`code-${id}`}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
        </>
      )}
      <p>
        <button className="primary" type="submit">
          Approve
        </button>
      </p>
      <p role="status">{msg}</p>
    </form>
  );
}

/** ADM-095 point 2: the option-worth pass of the impact note, run with the proposed value. */
/**
 * 26 ADM-081: for a key that feeds the one cap, the harness's run with the proposed value: the weeks of the free
 * player and of the fastest spender to a 10 handicap, the time saved and the gap after a year, then whether it
 * breaks the one cap. The server holds the verdict (breaks_one_cap) and keeps Approve off while it does.
 */
function OneCapCheck({ run }: { run?: OneCapRun }) {
  if (!run) return <p className="muted">{adminText('config.one_cap_not_run')}</p>;
  const line = pct(run.line ?? 0.4);
  // the verdict first, with the one figure it turns on; the weeks and the shots under it
  return (
    <>
      <p className={run.breaks_one_cap ? 'warn' : undefined}>
        {adminText(run.breaks_one_cap ? 'config.one_cap_breaks' : 'config.one_cap_inside', {
          saved: pct(run.time_saved),
          line,
        })}
      </p>
      <p>
        {adminText('config.one_cap_figures', {
          free: run.free_weeks.toFixed(1),
          spender: run.spender_weeks.toFixed(1),
          gap: run.gap_shots.toFixed(1),
        })}
      </p>
    </>
  );
}

function ThoughtCheck({ ow, line }: { ow?: OptionWorth; line: number }) {
  const shares = ow?.status === 'finished' ? ow.thought_shares : undefined;
  if (!shares)
    return <p className="muted">{adminText('config.thoughts_not_run', { line: pct(line) })}</p>;
  const share = (t: string): number | null => {
    const s = shares[t];
    return typeof s === 'number' ? s : null;
  };
  const named = (t: string) => {
    const s = share(t);
    return adminText('config.thought_share', {
      name: thoughtName(t),
      share: s === null ? '—' : pct(s),
    });
  };
  // A thought with no share counts as under the line, as in the table and on the server.
  const under = THOUGHTS.filter((t) => {
    const s = share(t);
    return s === null || s < line;
  });
  const lowest = [...THOUGHTS].sort((a, b) => (share(a) ?? -1) - (share(b) ?? -1))[0];
  // It leads with a sentence; the table of all twelve is folded below it (DES-561 item 6).
  return (
    <>
      <p className={under.length > 0 ? 'warn' : undefined}>
        {under.length > 0
          ? adminText('config.thoughts_under', {
              n: under.length,
              line: pct(line),
              names: under.map(named).join(', '),
            })
          : adminText('config.thoughts_all_pass', {
              line: pct(line),
              name: lowest ? thoughtName(lowest) : '—',
              share: lowest && share(lowest) !== null ? pct(share(lowest)!) : '—',
            })}
      </p>
      <details>
        <summary>{adminText('config.thoughts_see_all')}</summary>
        <ThoughtTable
          shares={shares}
          line={line}
          caption={adminText('config.thoughts_caption', { line: pct(line) })}
        />
      </details>
    </>
  );
}

/** One request that waits alone: its facts (ADM-080), its note (ADM-022) and its checks. */
function RequestCard({
  sb,
  c,
  check,
  onDone,
}: {
  sb: SupabaseClient;
  c: ChangeRow;
  check?: Check;
  onDone: () => void;
}) {
  const k = factsOf(c.config_keys);
  const thought = c.key.startsWith('shot.thought.');
  const oneCap = check?.proofs.some((p) => p.proof_key === 'one_cap') ?? false;
  const id = c.id.slice(0, 8);
  return (
    <li className="card">
      <p className="req-key">
        {k?.is_fairness ? '⚖ ' : ''}
        <code>
          <Breakable text={c.key} />
        </code>
      </p>
      <div className="panels">
        <div>
          <dl className="facts">
            <dt>Now</dt>
            <dd>
              <code>{show(c.current_value)}</code>
            </dd>
            <dt>Asked for</dt>
            <dd>
              <code>{show(c.proposed_value)}</code>
            </dd>
            <dt>Allowed range</dt>
            <dd>{rangeText(k, oneCap)}</dd>
            <dt>Owner file</dt>
            <dd>{k?.owner_file ?? '—'}</dd>
            <dt>Reason</dt>
            <dd>{c.reason}</dd>
          </dl>
          <p className="muted">
            {c.impact_note?.text ?? 'No impact note yet. It is made when a change is asked for.'}
          </p>
        </div>
        <div>
          {check && check.proofs.length > 0 && (
            <>
              <p className="sub" id={`proofs-${id}`}>
                {adminText('config.proofs_label')}
              </p>
              <ul className="plain proofs" aria-labelledby={`proofs-${id}`}>
                {check.proofs.map((p) => (
                  <li key={p.proof_key}>
                    <ProofState state={p.state} /> {PROOF_NAMES[p.proof_key] ?? p.proof_key}
                  </li>
                ))}
              </ul>
            </>
          )}
          {thought && (
            <ThoughtCheck ow={c.impact_note?.option_worth} line={check?.thought_line ?? 0.05} />
          )}
          {oneCap && <OneCapCheck run={c.impact_note?.proofs?.one_cap} />}
          <ApproveForm sb={sb} c={c} onDone={onDone} blockedBy={check?.guard ?? undefined} />
        </div>
      </div>
    </li>
  );
}

type ListState = 'loading' | 'ready' | 'error';

/** A list's own line while it loads, or when it could not be read. Nothing once it is ready. */
function ListStatus({ state, onRetry }: { state: ListState; onRetry: () => void }) {
  if (state === 'loading')
    return (
      <p className="muted" role="status">
        {adminText('config.loading')}
      </p>
    );
  if (state === 'error')
    return (
      <>
        <p className="warn" role="alert">
          {adminText('config.load_failed')}
        </p>
        <p>
          <button type="button" onClick={onRetry}>
            {adminText('config.try_again')}
          </button>
        </p>
      </>
    );
  return null;
}

/** The server's checks, or none when the call fails (the server still refuses on its own). */
async function loadChecks(sb: SupabaseClient): Promise<Record<string, Check>> {
  try {
    const res = await sb.schema('ops').rpc('admin_config_checks');
    const data = (res as { data?: unknown } | undefined)?.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
    const out: Record<string, Check> = {};
    for (const [id, v] of Object.entries(data as Record<string, unknown>))
      if (v && typeof v === 'object' && Array.isArray((v as Check).proofs)) out[id] = v as Check;
    return out;
  } catch {
    return {};
  }
}

export function ConfigPage({ sb }: { sb: SupabaseClient }) {
  const [rows, setRows] = useState<ConfigRow[]>([]);
  const [queue, setQueue] = useState<ChangeRow[]>([]);
  const [checks, setChecks] = useState<Record<string, Check>>({});
  // Each list says plainly while it loads and when it could not be read (24 DES-561 item 8): an
  // empty list is never shown in place of one that has not arrived.
  const [rowsState, setRowsState] = useState<ListState>('loading');
  const [queueState, setQueueState] = useState<ListState>('loading');
  const load = () => {
    void sb
      .schema('ops')
      .from('game_config')
      .select('key,value,version,config_keys(is_fairness)')
      .eq('status', 'active')
      .order('key')
      .then(({ data, error }) => {
        if (error || !data) return setRowsState('error');
        setRows((data as unknown as ActiveRow[]).map(toConfigRow));
        setRowsState('ready');
      });
    void sb
      .schema('ops')
      .from('config_change_requests')
      .select(
        'id,key,kind,current_value,proposed_value,reason,status,impact_note,batch_code,config_keys(is_fairness,min_value,max_value,owner_file)',
      )
      .eq('status', 'pending')
      .then(({ data, error }) => {
        if (error || !data) return setQueueState('error');
        const q = data as ChangeRow[];
        setQueue(q);
        setQueueState('ready');
        // Only a ⚖ change can be stopped by a guard, so only then is the server asked.
        if (q.some((c) => !c.batch_code && isFair(c) && (c.kind ?? 'change') === 'change'))
          void loadChecks(sb).then(setChecks);
      });
  };
  useEffect(load, [sb]);
  const retry = () => {
    setRowsState('loading');
    setQueueState('loading');
    load();
  };
  const grouped = batches(queue);
  const single = queue.filter((c) => !c.batch_code);
  const fairSingle = single.filter(inGroup);
  const otherSingle = single.filter((c) => !inGroup(c));
  const cards = (list: ChangeRow[]) => (
    <ul className="plain">
      {list.map((c) => (
        <RequestCard key={c.id} sb={sb} c={c} check={checks[c.id]} onDone={load} />
      ))}
    </ul>
  );
  return (
    <>
      <section aria-labelledby="q">
        <h2 id="q">
          {queueState === 'ready'
            ? adminText('config.waiting_heading', { n: queue.length })
            : adminText('config.waiting_heading_plain')}
        </h2>
        <ListStatus state={queueState} onRetry={retry} />
        {queueState === 'ready' && queue.length === 0 && (
          <p className="muted">Nothing is waiting.</p>
        )}
        {[...grouped].map(([code, items]) => (
          <section key={code} className="card" aria-labelledby={`b-${code}`}>
            <h3 id={`b-${code}`}>{batchTitle(code)}</h3>
            <BatchApproveForm sb={sb} code={code} items={items} onDone={load} />
          </section>
        ))}
        {fairSingle.length > 0 && (
          <section aria-labelledby="fp">
            <h3 id="fp">{adminText('config.group_fair', { n: fairSingle.length })}</h3>
            <p className="muted">{adminText('config.group_fair_intro')}</p>
            {cards(fairSingle)}
          </section>
        )}
        {otherSingle.length > 0 && (
          <section aria-labelledby="oc">
            <h3 id="oc">{adminText('config.group_other', { n: otherSingle.length })}</h3>
            {cards(otherSingle)}
          </section>
        )}
      </section>
      <section aria-labelledby="c">
        <h2 id="c">
          {rowsState === 'ready'
            ? adminText('config.settings_heading', { n: rows.length })
            : adminText('config.settings_heading_plain')}
        </h2>
        <p className="muted">
          ⚖ marks a fairness or money setting. It changes only after you approve it.
        </p>
        <ListStatus state={rowsState} onRetry={retry} />
        {rowsState === 'ready' && rows.length === 0 && (
          <p className="muted">{adminText('config.settings_none')}</p>
        )}
        {rowsState === 'ready' && rows.length > 0 && (
          <table>
            <thead>
              <tr>
                <th scope="col">Setting</th>
                <th scope="col">Value</th>
                <th scope="col">Version</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td className={r.fair ? 'fair' : undefined}>
                    {r.fair ? '⚖ ' : ''}
                    <code>
                      <Breakable text={r.key} />
                    </code>
                  </td>
                  <td>
                    <code>
                      <Breakable text={JSON.stringify(r.value)} at={VALUE_BREAKS} />
                    </code>
                  </td>
                  <td>{r.version}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
